import React, { FC, Fragment, ReactNode, useState } from 'react';
import { QueryResult } from 'react-apollo';
import { FetchMoreOptions } from 'apollo-client';

import {
  GetFollowersComponent,
  PaginatedFollowedByUsers,
  PaginatedFollowedByUsersInput,
  PublicUserProfile,
} from 'api';

export interface IGetFollowers {
  followers: PublicUserProfile[];
  count: number;
}

interface IProps {
  children: (props: IGetFollowers) => ReactNode;
  variables: PaginatedFollowedByUsersInput;
  autoPaginate?: boolean;
}

const GetFollowers: FC<IProps> = ({ autoPaginate, variables, children }) => {
  const [followedByUserId, setFollowedByUserId] = useState();
  const queryKey = 'getPaginatedFollowedByUsers';

  const updateQuery: FetchMoreOptions['updateQuery'] = (prev, options) => {
    const { fetchMoreResult } = options;

    if (!fetchMoreResult) {
      return prev;
    }

    return {
      [queryKey]: {
        ...fetchMoreResult[queryKey],
        followed_by_users: [
          ...prev[queryKey].followed_by_users,
          ...fetchMoreResult[queryKey].followed_by_users,
        ],
      },
    };
  };

  function onLoadMore(queryResult: QueryResult) {
    const { data, fetchMore } = queryResult;
    const { followed_by_user_id, username }: PaginatedFollowedByUsers = data[queryKey];

    return fetchMore({
      updateQuery,
      variables: {
        data: {
          ...variables,
          followed_by_user_id,
          username,
        },
      },
    });
  }

  return (
    <GetFollowersComponent variables={{ data: variables }}>
      {queryResult => {
        const { data, loading } = queryResult;
        if (loading) {
          return (
            <Fragment>
              {children({
                followers: [],
                count: 0,
              })}
            </Fragment>
          );
        }

        if (!data || !data[queryKey]) {
          return null;
        }

        const { followed_by_user_id, followed_by_users, count } = data[queryKey];
        const isFinished = !loading;
        const canPaginate = !!followed_by_user_id;
        const hasChanged = followed_by_user_id !== followedByUserId;

        if (autoPaginate && isFinished && canPaginate && hasChanged) {
          onLoadMore(queryResult);
          setFollowedByUserId(followedByUserId);
        }

        return (
          <Fragment>
            {children({
              followers: followed_by_users as PublicUserProfile[],
              count,
            })}
          </Fragment>
        );
      }}
    </GetFollowersComponent>
  );
};

GetFollowers.defaultProps = {
  autoPaginate: false,
};

export default GetFollowers;
